import { UserManager, User, UserManagerSettings, Log, UserSettings } from "oidc-client";
import { SigninRequest, OidcClient } from "oidc-client";
import { singleton } from "aurelia-dependency-injection";
import oidcConfig from "../oidc-settings";
import { ClaimsHelper } from "./claims-helper";
import { AppUser } from "./app-user";
import { AppSettings } from "../app-settings";
import { NetworkError, IpAuthenticationError, UrlCredentialsAuthenticationError } from "./exceptions";

// We need to include cookies on CORS requests to 
// identity server to make it work with EZProxy
// tslint:disable-next-line: only-arrow-functions
(function(open) {
    XMLHttpRequest.prototype.open = function(method, url) {
        if (url.startsWith(AppSettings.IdentityApiUrlPrefix)) {
            this.withCredentials = true;
        }
        open.apply(this, arguments);
    };
})(XMLHttpRequest.prototype.open);

export class AuthService {

    public AppUser: AppUser;
    public oidcUser: User;
    private userManager: UserManager;
    private localStorageTimer: number;

    constructor() {
        Log.logger = console;
        this.AppUser = new AppUser();
        this.userManager = new UserManager(oidcConfig);
        this.userManager.clearStaleState();
        this.setupEvents();
    }

    public async startAuthentication(returnUrl = "", override = false): Promise<void> {
        return await this.userManager.signinRedirect(
            {
                state: { returnUrl },
            extraQueryParams : { override, prompt : "login" }
        });
    }

    public async activeDirectoryDirectSignIn(returnUrl = "") {
        await this.userManager.signinRedirect({
            acr_values: "idp:ActiveDirectory",
            state: { returnUrl },
            extraQueryParams : { prompt : "login" }
        });
    }

    public async wayflessSignIn(entityId: string, returnUrl = "") {
        await this.userManager.signinRedirect({
            acr_values: "idp:Wayfless",
            state: { returnUrl },
            extraQueryParams : { entityId, prompt : "login" }
        });
    }

    public async prepare(silent = true) {
        this.oidcUser = await this.userManager.getUser();
        if (this.oidcUser) {
            if (this.oidcUser.expired) {
                try {
                    await this.userManager.signinSilent();
                } catch (err) {
                    this.AppUser.reset();
                    await this.userManager.removeUser();
                    window.location.reload();
                }
            }
            await this.populateAppUser(silent);
        } else {
            this.oidcUser = await this.attemptIpRecognition();
            if (this.oidcUser) {
                await this.userManager.storeUser(this.oidcUser);
                await this.populateAppUser(silent);
            }
        }
    }

    public async signInWithUrlCredentials(username: string, password: string) {
        try {
            const auth = await this.authoriseWithUrlCredentials(username, password);
            const profile = await this.getUserInfo(auth);
            auth.profile = profile;
            this.oidcUser = new User(auth);
            await this.userManager.storeUser(this.oidcUser);
            this.populateAppUser(false);
        } catch (err) {
            await this.userManager.removeUser();
            this.AppUser.reset();
            throw new UrlCredentialsAuthenticationError(err.message);
        }
    }

    public async signOut() {
        this.AppUser.reset();
        // If we have an id_token, redirect to Identity Server to sign out there too
        // If we don't, just clear up here, as Identity Server needs an id_token to redirect here.
        // This may be the case when using custom grant types.
        if (this.oidcUser.id_token) {
            console.log("NO ID");
            await this.userManager.signoutRedirect({post_logout_redirect_uri: AppSettings.WindowOrigin });
        } else {
            await this.userManager.removeUser();
            window.location.reload();
        }
    }

    private async populateAppUser(silent: boolean) {
        if (this.oidcUser) {
            const props = ClaimsHelper.MapClaimsToUser(this.oidcUser.profile);
            this.AppUser.set(props);
            this.setupLocalStorageListener();
            if (!silent) {
                this.AppUser.trigger("refresh");
            }
        }
    }

    private setupEvents() {
        this.userManager.events.addUserLoaded(async () => {
            this.oidcUser = await this.userManager.getUser();
            await this.populateAppUser(true);
        });

        this.userManager.events.addSilentRenewError(async () => {
            await this.signOut();
        });

        this.userManager.events.addAccessTokenExpiring(() => {
            console.log("EXPIRING");
        });

        // this.userManager.events.addAccessTokenExpired(() => {
        //     console.log("EXPIRED");
        // });
    }

    private setupLocalStorageListener() {
        if (this.localStorageTimer) {
            clearInterval(this.localStorageTimer);
        }
        this.localStorageTimer = setInterval(async () => {
            const keys = await oidcConfig.userStore.getAllKeys();
            if (keys.length === 0) {
                location.reload();
            }
        }, 2000);
    }

    private async attemptIpRecognition() {
        const recognised = await this.checkForIpRecognition();
        if (recognised) {
            return await this.signInWithIp();
        } else {
            return null;
        }
    }

    private async checkForIpRecognition() {
        if (!window.navigator.onLine) {
            return false;
        }
        try {
            const request = $.ajax({
                url : AppSettings.ApiUrlPrefix + "isiprecognised",
                type: "GET"
            });
            return await Promise.resolve(request);
        } catch (err) {
            throw new NetworkError();
        }
    }

    private async getUserInfo(user: UserSettings) {
        const url = AppSettings.IdentityApiUrlPrefix + "connect/userinfo";
        const request = $.ajax({url, type: "POST", xhrFields: {
            withCredentials: true
        }, beforeSend : (xhr) => {
            xhr.setRequestHeader("Authorization", `${user.token_type} ${user.access_token}`);
        }});
        return await Promise.resolve(request);
    }

    private async authoriseWithIp(): Promise<UserSettings> {
        const url = AppSettings.ApiUrlPrefix + "connect/token";
        const data = { grant_type: "iprec", client_id: "justisoneweb" };
        const request = $.ajax({
            url : url,
            type: "POST",
            data: data
        });
        const response = await Promise.resolve(request);
        const now = Date.now() / 1000;
        response.expires_at = now + response.expires_in;
        return response;
    }

    private async authoriseWithUrlCredentials(username: string, password: string) {
        const url = AppSettings.IdentityApiUrlPrefix + "connect/token";
        const data = { grant_type: "url_credentials", username, password, client_id: "justisoneweb" };
        const request = $.ajax({
            url : url,
            type: "POST",
            data: data,
            xhrFields: {
                withCredentials: true
            }
        });
        const response = await Promise.resolve(request);
        const now = Date.now() / 1000;
        response.expires_at = now + response.expires_in;
        return response;
    }

    private async signInWithIp(returnUrl?: string): Promise<User> {
        try {
            $(".loading-msg").text("Attempting IP recognition...");
            const auth = await this.authoriseWithIp();
            const profile = await this.getUserInfo(auth);
            auth.profile = profile;
            const user = new User(auth);
            return user;
        } catch (err) {
            $(".loading-msg").text("Loading application...");
            await this.userManager.removeUser();
            this.AppUser.reset();
            throw new IpAuthenticationError(err.message);
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/authentication/auth-service.ts